import { createAsyncThunk } from '@reduxjs/toolkit';

import { HttpError } from '../../api/http-error';
import { t } from '../../i18n';
import type { ApiError } from '../../models/api-error.enum';
import { parseApiError } from '../../models/api-error.enum';
import type { AlbumAccessResponse, DownloadFormatType } from '../models/album.interface';
import type { DownloadResponse } from '../services/music.service';
import { musicService } from '../services/music.service';

export const DEMO_ALBUM_ID = '__demo__';

export const loadAlbum = createAsyncThunk<AlbumAccessResponse, string, { rejectValue: ApiError }>(
  'music/loadAlbum',
  async (albumId, { rejectWithValue }) => {
    try {
      return await musicService.loadAlbum(albumId);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const loadAlbumByProductId = createAsyncThunk<
  AlbumAccessResponse,
  string,
  { rejectValue: ApiError }
>('music/loadAlbumByProductId', async (productId, { rejectWithValue }) => {
  try {
    return await musicService.loadAlbumByProductId(productId);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const loadDemoAlbum = createAsyncThunk<AlbumAccessResponse, void>(
  'music/loadDemoAlbum',
  async () => musicService.loadDemoAlbum()
);

export const downloadAlbum = createAsyncThunk<
  DownloadResponse,
  { albumId: string; format: DownloadFormatType },
  { rejectValue: string }
>('music/downloadAlbum', async ({ albumId, format }, { rejectWithValue }) => {
  try {
    return await musicService.downloadAlbum(albumId, format);
  } catch (error) {
    // The API tells these apart so the limit message only shows when it is true (SNGL-6923):
    // 409 the code is used up, 403 the signed-in fan has no access, 404 no usable download code.
    if (error instanceof HttpError) {
      if (error.status === 409) {
        return rejectWithValue(t('music.downloadLimitReached'));
      }
      if (error.status === 403) {
        return rejectWithValue(t('music.downloadNotAllowed'));
      }
      if (error.status === 404) {
        return rejectWithValue(t('music.downloadCodeUnavailable'));
      }
    }
    return rejectWithValue(t('music.downloadFailed'));
  }
});
